import { useState, FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { authService } from '@shared/services/api';
import AuthLayout from '@shared/layouts/AuthLayout';
import { Card } from '@shared/components/ui/Card';
import { Button } from '@shared/components/ui/Button';
import { Input } from '@shared/components/ui/Input';

export default function ForgotPassword() {
  const { t } = useTranslation('auth');
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await authService.forgotPassword({ email });
      setSuccess(true);
    } catch {
      setError(t('forgotPassword.errorMessage'));
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <AuthLayout>
        <Card className="p-8 text-center animate-in fade-in zoom-in duration-500">
          <div
            aria-hidden="true"
            className="mx-auto mb-8 flex h-20 w-20 items-center justify-center rounded-2xl border border-state-ok-light/20 bg-state-ok-bg-light dark:border-state-ok-dark/20 dark:bg-state-ok-bg-dark"
          >
            <svg
              className="h-10 w-10 text-state-ok-light dark:text-state-ok-dark"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={3}
                d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
              />
            </svg>
          </div>
          <h2 className="mb-2 text-2xl font-bold tracking-tight text-text-primary-light dark:text-text-primary-dark sm:text-3xl">
            {t('forgotPassword.successTitle')}
          </h2>
          <p
            role="status"
            aria-live="polite"
            className="mb-8 text-sm leading-6 text-text-muted-light dark:text-text-muted-dark sm:mb-10"
          >
            {t('forgotPassword.successMessage', { email })}
          </p>
          <Link
            to="/login"
            className="text-sm font-semibold text-brand-600 hover:underline dark:text-brand-400"
          >
            {t('forgotPassword.backToLogin')}
          </Link>
        </Card>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      <Card className="p-8">
        <div className="text-center mb-10">
          <h2 className="text-2xl font-bold tracking-tight text-text-primary-light dark:text-text-primary-dark sm:text-3xl">
            {t('forgotPassword.title')}
          </h2>
          <p className="mt-2 text-sm leading-6 text-text-muted-light dark:text-text-muted-dark">
            {t('forgotPassword.subtitle')}
          </p>
        </div>

        <form className="space-y-6" onSubmit={handleSubmit}>
          {error && (
            <div
              role="alert"
              className="rounded-xl border border-state-danger-light/20 border-l-4 bg-state-danger-bg-light p-4 dark:border-state-danger-dark/20 dark:bg-state-danger-bg-dark"
            >
              <p className="text-sm font-bold text-state-danger-light dark:text-state-danger-dark">
                {error}
              </p>
            </div>
          )}

          <Input
            label={t('forgotPassword.emailLabel')}
            id="email"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={t('forgotPassword.emailPlaceholder')}
            autoComplete="email"
          />

          <Button type="submit" className="w-full" isLoading={loading}>
            {loading ? t('forgotPassword.submitting') : t('forgotPassword.submitButton')}
          </Button>

          <div className="text-center mt-8">
            <Link
              to="/login"
              className="text-sm font-semibold text-text-muted-light transition-colors hover:text-brand-600 dark:text-text-muted-dark dark:hover:text-brand-400"
            >
              {t('forgotPassword.backToLogin')}
            </Link>
          </div>
        </form>
      </Card>
    </AuthLayout>
  );
}
